/**
 * Audit Log (prototype v108 P 1350–1358; H§15): the immutable history of sensitive changes. Summary tiles, search,
 * User / Department / Action / Entity filters and a date range, Export CSV, and Date · User · Action · Entity ·
 * Change · Before / After · Source. Rows come from audit_logs and admin_audit_log (src/lib/staging/auditView.js), and
 * every pipeline stage change from booking_stage_history (Traci's doc, Oct 2026).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/admin/ui/button';
import { Input } from '@/components/admin/ui/input';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { fetchInBatches } from '@utils/batchSupabaseQueries';
import { fromAuditLog, fromAdminAuditLog, fromStageHistory, filterAudit, auditSummary, auditCsv, CATEGORIES } from '@/lib/staging/auditView';
import { STAGES } from '@/lib/booking/lifecycleView';
import { cleanStageHistory } from '@/lib/booking/stageHistory';
import { ROLE_LABELS, STAGING_ROOT } from '@/lib/staging/roleNav';
import { formatWhen } from '@/lib/notifications/center';

const PAGE = 50;
const ROLE_ORDER = ['admin', 'billing', 'sales', 'editing', 'talent_coordinator'];

function entityLink(r: any) {
  if (!r.entity_id) return null;
  if (r.entity === 'Booking') return `${STAGING_ROOT}/bookings/${r.entity_id}`;
  if (r.entity === 'Talent') return `${STAGING_ROOT}/talent/${r.entity_id}`;
  if (r.entity === 'Client') return `${STAGING_ROOT}/clients/${r.entity_id}`;
  return null;
}

export default function AuditPage() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [f, setF] = useState({ q: '', user: '', department: '', category: '', entity: '', from: '', to: '' });
  const [page, setPage] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const db = supabaseAdmin as any;
        const [a, b, s] = await Promise.all([
          db.from('audit_logs').select('*').order('created_at', { ascending: false }).limit(2000),
          db.from('admin_audit_log').select('*, actor:users!admin_audit_log_actor_id_fkey(full_name), subject:users!admin_audit_log_user_id_fkey(full_name)').order('created_at', { ascending: false }).limit(1000),
          db.from('booking_stage_history').select('id, booking_id, from_stage, to_stage, changed_by, changed_at, change_type, reason, trigger_source').order('changed_at', { ascending: false }).limit(2000)
        ]);
        if (a.error) throw a.error;
        const stageRows = cleanStageHistory(s.error ? [] : s.data || []) as any[];
        const ids = [...new Set([...(a.data || []).map((r: any) => r.user_id), ...((b.data || []) as any[]).map((r: any) => r.actor_id), ...stageRows.map((r) => r.changed_by)].filter(Boolean))];
        const roleRows = ids.length ? (await fetchInBatches(db, 'user_roles', 'user_id, role', 'user_id', ids)).data || [] : [];
        const byUser = new Map<string, string>();
        for (const role of ROLE_ORDER) for (const r of roleRows as any[]) if (r.role === role && !byUser.has(r.user_id)) byUser.set(r.user_id, ROLE_LABELS[role as keyof typeof ROLE_LABELS] || role);
        const changers = stageRows.some((r) => r.changed_by) ? (await fetchInBatches(db, 'users', 'id, full_name', 'id', [...new Set(stageRows.map((r) => r.changed_by).filter(Boolean))])).data || [] : [];
        const names = new Map<string, string>((changers as any[]).map((u) => [u.id, u.full_name]));
        const stageLabel = (k: string) => (STAGES as any[]).find((x) => x.key === k)?.label || String(k || '').replace(/_/g, ' ');
        const all = [...(a.data || []).map((r: any) => fromAuditLog(r, byUser)), ...(b.error ? [] : (b.data || []).map((r: any) => fromAdminAuditLog(r, byUser))), ...stageRows.map((r) => fromStageHistory(r, byUser, names, stageLabel))]
          .sort((x, y) => String(y.at || '').localeCompare(String(x.at || '')));
        setRows(all);
      } catch (e: any) { setError(e.message || String(e)); }
    })();
  }, []);

  const shown = useMemo(() => filterAudit(rows || [], f), [rows, f]);
  const sum = auditSummary(shown);
  const users = useMemo(() => [...new Set((rows || []).map((r) => r.user))].sort(), [rows]);
  const depts = useMemo(() => [...new Set((rows || []).map((r) => r.department))].sort(), [rows]);
  const entities = useMemo(() => [...new Set((rows || []).map((r) => r.entity).filter((x) => x && x !== '—'))].sort(), [rows]);
  const set = (k: keyof typeof f, v: string) => { setF({ ...f, [k]: v }); setPage(0); };
  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([auditCsv(shown)], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url; link.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };
  const sel = 'h-9 rounded-md border bg-background px-2 text-sm';

  return (
    <div className="p-8 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Audit Log</h2>
          <p className="text-sm text-muted-foreground mt-1">Immutable history of sensitive configuration, record, permission, lifecycle, flag, and financial changes.</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!shown.length}><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[['Events in Selected Range', sum.total, 'All matching audit records'], ['Manual Financial Actions', sum.financial, 'Payments, charges, refunds'], ['Configuration Changes', sum.configuration, 'Pricing, rules, promotions'], ['Permission / User Changes', sum.permission, 'User access and roles']].map(([label, n, hint]) => (
          <div key={label as string} className="rounded-xl border bg-card p-4 shadow-sm"><div className="text-xs text-muted-foreground">{label}</div><b className="mt-1 block text-2xl tabular-nums">{rows ? n : '—'}</b><div className="text-[11px] text-muted-foreground">{hint}</div></div>
        ))}
      </div>
      <section className="rounded-xl border bg-card p-5 shadow-sm space-y-3">
        <div><h3 className="font-semibold">System History</h3><p className="text-xs text-muted-foreground">Search and filter by who changed what, when, and where it came from.</p></div>
        <div className="flex flex-wrap gap-2">
          <Input aria-label="Search audit log" className="w-72" placeholder="Search user, action, client, booking, talent..." value={f.q} onChange={(e) => set('q', e.target.value)} />
          <select aria-label="User" className={sel} value={f.user} onChange={(e) => set('user', e.target.value)}><option value="">All Users</option>{users.map((u) => <option key={u}>{u}</option>)}</select>
          <select aria-label="Department" className={sel} value={f.department} onChange={(e) => set('department', e.target.value)}><option value="">All Departments</option>{depts.map((d) => <option key={d}>{d}</option>)}</select>
          <select aria-label="Action" className={sel} value={f.category} onChange={(e) => set('category', e.target.value)}><option value="">All Actions</option>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
          <select aria-label="Entity" className={sel} value={f.entity} onChange={(e) => set('entity', e.target.value)}><option value="">All Entities</option>{entities.map((x) => <option key={x}>{x}</option>)}</select>
          <Input aria-label="From date" type="date" className="w-40" value={f.from} onChange={(e) => set('from', e.target.value)} />
          <Input aria-label="To date" type="date" className="w-40" value={f.to} onChange={(e) => set('to', e.target.value)} />
        </div>
        {error && <p className="text-sm text-destructive">Could not load: {error}</p>}
        {!rows && !error && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {rows && (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground"><tr><th className="px-3 py-2">Date / Time</th><th className="px-3 py-2">User</th><th className="px-3 py-2">Action</th><th className="px-3 py-2">Entity</th><th className="px-3 py-2">Change</th><th className="px-3 py-2">Before / After</th><th className="px-3 py-2">Source</th></tr></thead>
              <tbody className="divide-y">
                {shown.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No audit records match these filters.</td></tr>}
                {shown.slice(page * PAGE, page * PAGE + PAGE).map((r) => {
                  const to = entityLink(r);
                  return (
                    <tr key={r.id} className="align-top">
                      <td className="px-3 py-2 whitespace-nowrap text-xs">{formatWhen(r.at)}</td>
                      <td className="px-3 py-2"><span className="block">{r.user}</span><span className="block text-[11px] text-muted-foreground">{r.department}</span></td>
                      <td className="px-3 py-2"><span className="block">{r.action}</span><span className="block text-[11px] text-muted-foreground">{r.category}</span></td>
                      <td className="px-3 py-2"><span className="block">{r.entity}</span>{r.entity_id && (to ? <Link className="block font-mono text-[11px] underline decoration-dotted" to={to}>{String(r.entity_id).slice(0, 8).toUpperCase()}</Link> : <span className="block font-mono text-[11px] text-muted-foreground">{String(r.entity_id).slice(0, 8).toUpperCase()}</span>)}</td>
                      <td className="px-3 py-2 max-w-[320px] break-words text-xs">{r.change || '—'}</td>
                      <td className="px-3 py-2 max-w-[260px] break-words text-[11px]">{r.before || r.after ? <><span className="block text-muted-foreground">Before: {r.before || '—'}</span><span className="block">After: {r.after || '—'}</span></> : '—'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{r.source}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {shown.length > PAGE && (
          <div className="flex items-center justify-end gap-2 text-sm">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</Button>
            <span className="tabular-nums text-muted-foreground">{page * PAGE + 1}–{Math.min(shown.length, page * PAGE + PAGE)} of {shown.length}</span>
            <Button size="sm" variant="outline" disabled={(page + 1) * PAGE >= shown.length} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        )}
      </section>
    </div>
  );
}
